import React, { useState, useEffect, useRef, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import axios from "axios";
import { API_BASE_URL, getApiToken } from "../config/authConfig";

async function fetchWithRetry(fn, retries = 3, delayMs = 1500) {
  for (let i = 0; i < retries; i++) {
    try {
      return await fn();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 503 && i < retries - 1) {
        await new Promise((r) => setTimeout(r, delayMs * (i + 1)));
      } else {
        throw err;
      }
    }
  }
}

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const LOCATIONS = ["Philadelphia", "New York City", "Bakery", "Remote"];
const REMOTE_OFFICE = { OfficeName: "Others", Location: "Remote", microsoft365Groups: [], securityGroups: [], distributionGroups: [] };

function ReadOnlyField({ label, value, placeholder = "—" }) {
  return (
    <div className="form-field">
      <label className="field-label">{label}</label>
      <div className="field-readonly">{value || placeholder}</div>
    </div>
  );
}

// Generic live-search dropdown for employees and managers
function UserSearchDropdown({ label, value, onChange, onSelect, results, loading, open, placeholder }) {
  return (
    <div className="form-field">
      <label className="field-label">{label}</label>
      <div className="search-wrapper">
        <input
          type="text"
          className="field-input"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="off"
        />
        {loading && <span className="search-spinner" />}
      </div>
      {open && results.length > 0 && (
        <ul className="search-dropdown">
          {results.map((u) => (
            <li key={u.id || u.userPrincipalName} className="search-result" onMouseDown={() => onSelect(u)}>
              <span className="result-name">{u.displayName}</span>
              <span className="result-upn">{u.userPrincipalName}</span>
            </li>
          ))}
        </ul>
      )}
      {open && results.length === 0 && !loading && (
        <ul className="search-dropdown">
          <li className="search-result search-no-results">No results found</li>
        </ul>
      )}
    </div>
  );
}

// Searchable list dropdown (departments, job titles, offices)
function SearchableListDropdown({ label, value, onChange, onSelect, items, loading, open, setOpen, placeholder, disabled, wrapperRef }) {
  const filtered = items.filter((i) =>
    i.toLowerCase().includes(value.toLowerCase())
  );
  return (
    <div className="form-field" ref={wrapperRef}>
      <label className="field-label">{label}</label>
      <div className="search-wrapper">
        <input
          type="text"
          className={`field-input${disabled ? " field-input-disabled" : ""}`}
          placeholder={disabled ? "Select department first" : placeholder}
          value={value}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => !disabled && setOpen(true)}
          autoComplete="off"
          disabled={disabled}
        />
        {loading && <span className="search-spinner" />}
      </div>
      {open && !disabled && filtered.length > 0 && (
        <ul className="search-dropdown">
          {filtered.map((item) => (
            <li key={item} className="search-result" onMouseDown={() => { onSelect(item); setOpen(false); }}>
              <span className="result-name">{item}</span>
            </li>
          ))}
        </ul>
      )}
      {open && !disabled && filtered.length === 0 && !loading && (
        <ul className="search-dropdown">
          <li className="search-result search-no-results">No results found</li>
        </ul>
      )}
    </div>
  );
}

export default function MovementForm({ onSubmitted, onViewHistory }) {
  const { accounts, instance } = useMsal();
  const user = accounts[0] || {};
  const submittedBy = user.username || "";

  const [token, setToken] = useState(null);
  const [step, setStep] = useState("employee"); // "employee" | "new-values" | "review"

  // ── Employee search ──────────────────────────────────────────────────────
  const [empQuery, setEmpQuery] = useState("");
  const [empResults, setEmpResults] = useState([]);
  const [empLoading, setEmpLoading] = useState(false);
  const [empOpen, setEmpOpen] = useState(false);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [empDetails, setEmpDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const empWrapperRef = useRef(null);
  const debouncedEmpQuery = useDebounce(empQuery, 350);

  // Old group mappings (resolved after employee selected)
  const [oldTitleMapping, setOldTitleMapping] = useState(null);
  const [oldOfficeMapping, setOldOfficeMapping] = useState(null);

  // Resolved display names for new selections
  const [newTitleGroupNames, setNewTitleGroupNames] = useState({});
  const [newOfficeGroupNames, setNewOfficeGroupNames] = useState({});

  // ── New values — Department ──────────────────────────────────────────────
  const [departments, setDepartments] = useState([]);
  const [deptSearch, setDeptSearch] = useState("");
  const [deptOpen, setDeptOpen] = useState(false);
  const [selectedDept, setSelectedDept] = useState("");
  const [loadingDepts, setLoadingDepts] = useState(false);
  const deptRef = useRef(null);

  // ── New values — Job Title ───────────────────────────────────────────────
  const [allTitleMappings, setAllTitleMappings] = useState([]);
  const [titleSearch, setTitleSearch] = useState("");
  const [titleOpen, setTitleOpen] = useState(false);
  const [selectedTitleMapping, setSelectedTitleMapping] = useState(null);
  const [loadingTitles, setLoadingTitles] = useState(false);
  const titleRef = useRef(null);

  // ── New values — Manager ─────────────────────────────────────────────────
  const [managerQuery, setManagerQuery] = useState("");
  const [managerResults, setManagerResults] = useState([]);
  const [managerLoading, setManagerLoading] = useState(false);
  const [managerOpen, setManagerOpen] = useState(false);
  const [selectedManager, setSelectedManager] = useState(null);
  const managerRef = useRef(null);
  const debouncedManagerQuery = useDebounce(managerQuery, 350);

  // ── New values — Location & Office ──────────────────────────────────────
  const [newLocation, setNewLocation] = useState("");
  const [allOfficeMappings, setAllOfficeMappings] = useState([]);
  const [officeSearch, setOfficeSearch] = useState("");
  const [officeOpen, setOfficeOpen] = useState(false);
  const [selectedOfficeMapping, setSelectedOfficeMapping] = useState(null);
  const [loadingOffices, setLoadingOffices] = useState(false);
  const officeRef = useRef(null);

  // ── Submission ───────────────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});

  // ── Token ────────────────────────────────────────────────────────────────
  useEffect(() => { getApiToken(instance).then(setToken); }, [instance]);

  // ── Outside click handlers ───────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (empWrapperRef.current && !empWrapperRef.current.contains(e.target)) setEmpOpen(false);
      if (deptRef.current && !deptRef.current.contains(e.target)) setDeptOpen(false);
      if (titleRef.current && !titleRef.current.contains(e.target)) setTitleOpen(false);
      if (managerRef.current && !managerRef.current.contains(e.target)) setManagerOpen(false);
      if (officeRef.current && !officeRef.current.contains(e.target)) setOfficeOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Employee search ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!debouncedEmpQuery || debouncedEmpQuery.length < 3 || !token || selectedEmp) {
      setEmpResults([]); setEmpOpen(false); return;
    }
    let cancelled = false;
    setEmpLoading(true);
    axios.get(`${API_BASE_URL}/movement/searchEmployees`, {
      params: { query: debouncedEmpQuery },
      headers: { Authorization: `Bearer ${token}` },
    }).then((res) => {
      if (!cancelled) { setEmpResults(res.data.users || []); setEmpOpen(true); }
    }).catch(() => {}).finally(() => { if (!cancelled) setEmpLoading(false); });
    return () => { cancelled = true; };
  }, [debouncedEmpQuery, token, selectedEmp]);

  const resolveGroupNames = useCallback(async (ids) => {
    const uniqueIds = [...new Set((ids || []).filter(Boolean))];
    if (uniqueIds.length === 0 || !token) return {};
    try {
      const res = await axios.get(`${API_BASE_URL}/movement/resolveGroups`, {
        params: { ids: uniqueIds.join(",") },
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data.names || {};
    } catch {
      return {};
    }
  }, [token]);

  const fetchEmployeeDetails = useCallback(async (upn) => {
    if (!token) return;
    setLoadingDetails(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/movement/getEmployeeDetails`, {
        params: { upn },
        headers: { Authorization: `Bearer ${token}` },
      });
      const details = res.data.user;
      setEmpDetails(details);
      // SP mappings resolved server-side — use directly
      setOldTitleMapping(details.currentTitleMapping || null);
      setOldOfficeMapping(details.currentOfficeMapping || null);
    } catch {
      setEmpDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  }, [token]);

  const handleEmpSelect = (u) => {
    setSelectedEmp(u);
    setEmpQuery(u.displayName);
    setEmpResults([]);
    setEmpOpen(false);
    setEmpDetails(null);
    setOldTitleMapping(null);
    setOldOfficeMapping(null);
    fetchEmployeeDetails(u.userPrincipalName);
    setValidationErrors((prev) => ({ ...prev, employee: undefined }));
  };

  const handleEmpQueryChange = (val) => {
    setEmpQuery(val);
    if (selectedEmp) {
      setSelectedEmp(null);
      setEmpDetails(null);
      setOldTitleMapping(null);
      setOldOfficeMapping(null);
    }
  };

  // ── Departments ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!token || step !== "new-values") return;
    setLoadingDepts(true);
    fetchWithRetry(() =>
      axios.get(`${API_BASE_URL}/movement/getDepartments`, {
        headers: { Authorization: `Bearer ${token}` },
      })
    )
      .then((res) => setDepartments(res.data.departments || []))
      .catch(() => {})
      .finally(() => setLoadingDepts(false));
  }, [token, step]);

  const handleDeptSelect = (dept) => {
    setSelectedDept(dept);
    setDeptSearch(dept);
    setDeptOpen(false);
    setSelectedTitleMapping(null);
    setTitleSearch("");
    setAllTitleMappings([]);
    setNewTitleGroupNames({});
    setValidationErrors((prev) => ({ ...prev, department: undefined }));
    // Load job titles for new dept
    setLoadingTitles(true);
    axios.get(`${API_BASE_URL}/movement/getJobTitles`, {
      params: { department: dept },
      headers: { Authorization: `Bearer ${token}` },
    }).then((res) => setAllTitleMappings(res.data.jobTitles || []))
      .catch(() => {})
      .finally(() => setLoadingTitles(false));
  };

  const handleTitleSelect = async (titleObj) => {
    setSelectedTitleMapping(titleObj);
    setTitleSearch(titleObj.title);
    setTitleOpen(false);
    setValidationErrors((prev) => ({ ...prev, jobTitle: undefined }));
    const ids = [
      ...(titleObj.microsoft365Groups || []),
      ...(titleObj.securityGroups || []),
      ...(titleObj.distributionGroups || []),
      ...(titleObj.licenseGroup ? [titleObj.licenseGroup] : []),
    ];
    const names = await resolveGroupNames(ids);
    setNewTitleGroupNames(names);
  };

  // ── Manager search ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!debouncedManagerQuery || debouncedManagerQuery.length < 3 || !token) {
      setManagerResults([]); setManagerOpen(false); return;
    }
    let cancelled = false;
    setManagerLoading(true);
    axios.get(`${API_BASE_URL}/movement/getManagers`, {
      params: { query: debouncedManagerQuery },
      headers: { Authorization: `Bearer ${token}` },
    }).then((res) => {
      if (!cancelled) { setManagerResults(res.data.managers || []); setManagerOpen(true); }
    }).catch(() => {}).finally(() => { if (!cancelled) setManagerLoading(false); });
    return () => { cancelled = true; };
  }, [debouncedManagerQuery, token]);

  const handleManagerSelect = (m) => {
    setSelectedManager(m);
    setManagerQuery(m.displayName);
    setManagerResults([]);
    setManagerOpen(false);
    setValidationErrors((prev) => ({ ...prev, manager: undefined }));
  };

  // ── Location & Office ────────────────────────────────────────────────────
  const handleLocationChange = (loc) => {
    setNewLocation(loc);
    setSelectedOfficeMapping(null);
    setOfficeSearch("");
    setAllOfficeMappings([]);
    setNewOfficeGroupNames({});
    setValidationErrors((prev) => ({ ...prev, location: undefined, office: undefined }));

    if (loc === "Remote") {
      setSelectedOfficeMapping(REMOTE_OFFICE);
      setOfficeSearch("Others");
      return;
    }
    if (!token) return;
    setLoadingOffices(true);
    axios.get(`${API_BASE_URL}/movement/getOffices`, {
      params: { location: loc },
      headers: { Authorization: `Bearer ${token}` },
    }).then(async (res) => {
      const offices = res.data.offices || [];
      setAllOfficeMappings(offices);
      // Auto-select if only one office
      if (offices.length === 1) {
        setSelectedOfficeMapping(offices[0]);
        setOfficeSearch(offices[0].OfficeName);
        const names = await resolveGroupNames([
          ...(offices[0].microsoft365Groups || []),
          ...(offices[0].securityGroups || []),
          ...(offices[0].distributionGroups || []),
        ]);
        setNewOfficeGroupNames(names);
      }
    }).catch(() => {}).finally(() => setLoadingOffices(false));
  };

  // ── Navigation ───────────────────────────────────────────────────────────
  const handleProceedToNewValues = () => {
    if (!selectedEmp || !empDetails) {
      setValidationErrors({ employee: "Please search for and select the employee." });
      return;
    }
    setStep("new-values");
  };

  const handleProceedToReview = () => {
    const errors = {};
    if (!selectedDept) errors.department = "New department is required.";
    if (!selectedTitleMapping) errors.jobTitle = "New job title is required.";
    if (!selectedManager) errors.manager = "New manager is required.";
    if (!newLocation) errors.location = "New location is required.";
    if (!selectedOfficeMapping) errors.office = "New office is required.";
    if (Object.keys(errors).length > 0) { setValidationErrors(errors); return; }
    setValidationErrors({});
    setStep("review");
  };

  // ── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError("");

    // Compute combined distribution groups (dept DLs + office DLs)
    const oldDeptDLs = oldTitleMapping?.distributionGroups || [];
    const oldOfficeDLs = oldOfficeMapping?.distributionGroups || [];
    const oldAllDLs = [...new Set([...oldDeptDLs, ...oldOfficeDLs])];

    const newDeptDLs = selectedTitleMapping?.distributionGroups || [];
    const newOfficeDLs = selectedOfficeMapping?.distributionGroups || [];
    const newAllDLs = [...new Set([...newDeptDLs, ...newOfficeDLs])];

    const payload = {
      employeeDisplayName: empDetails.displayName,
      employeeUPN: empDetails.userPrincipalName,
      oldDepartment: empDetails.department,
      newDepartment: selectedDept,
      oldJobTitle: empDetails.jobTitle,
      newJobTitle: selectedTitleMapping.title,
      oldManager: empDetails.managerUpn,
      newManager: selectedManager.userPrincipalName,
      oldLocation: empDetails.location || "",
      newLocation,
      oldOffice: empDetails.officeLocation,
      newOffice: selectedOfficeMapping.OfficeName,
      oldLicenseGroup: oldTitleMapping?.licenseGroup || "",
      newLicenseGroup: selectedTitleMapping.licenseGroup || "",
      oldMicrosoft365Groups: [...new Set([
        ...(oldTitleMapping?.microsoft365Groups || []),
        ...(oldOfficeMapping?.microsoft365Groups || []),
      ])].join(","),
      newMicrosoft365Groups: [...new Set([
        ...(selectedTitleMapping.microsoft365Groups || []),
        ...(selectedOfficeMapping?.microsoft365Groups || []),
      ])].join(","),
      oldSecurityGroups: [...new Set([
        ...(oldTitleMapping?.securityGroups || []),
        ...(oldOfficeMapping?.securityGroups || []),
      ])].join(","),
      newSecurityGroups: [...new Set([
        ...(selectedTitleMapping.securityGroups || []),
        ...(selectedOfficeMapping?.securityGroups || []),
      ])].join(","),
      oldDistributionGroups: oldAllDLs.join(","),
      newDistributionGroups: newAllDLs.join(","),
      submittedBy,
    };

    try {
      const res = await axios.post(`${API_BASE_URL}/movement/submit`, payload, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      onSubmitted({ ...payload, ...res.data });
    } catch (err) {
      setSubmitError(err.response?.data?.error || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Review helpers ───────────────────────────────────────────────────────
  function formatGroupCount(groups) {
    const n = (groups || []).length;
    if (n === 0) return "No Groups";
    return n === 1 ? "1 Group" : `${n} Groups`;
  }

  const oldDeptDLs = oldTitleMapping?.distributionGroups || [];
  const oldOfficeDLs = oldOfficeMapping?.distributionGroups || [];
  const oldAllDLs = [...new Set([...oldDeptDLs, ...oldOfficeDLs])];
  const newDeptDLs = selectedTitleMapping?.distributionGroups || [];
  const newOfficeDLs = selectedOfficeMapping?.distributionGroups || [];
  const newAllDLs = [...new Set([...newDeptDLs, ...newOfficeDLs])];

  // Old display names (resolved server-side by movementGetEmployeeDetails)
  const oldLicenseGroupName = oldTitleMapping?.licenseGroupName || oldTitleMapping?.licenseGroup || "None";
  const oldM365Names = [...new Set([
    ...(oldTitleMapping?.microsoft365GroupNames || []),
    ...(oldOfficeMapping?.microsoft365GroupNames || []),
  ])];
  const oldSecGroupNames = [...new Set([
    ...(oldTitleMapping?.securityGroupNames || []),
    ...(oldOfficeMapping?.securityGroupNames || []),
  ])];
  const oldDeptDLNames = oldTitleMapping?.distributionGroupNames || [];
  const oldOfficeDLNames = oldOfficeMapping?.distributionGroupNames || [];
  const oldAllDLNames = [...new Set([...oldDeptDLNames, ...oldOfficeDLNames])];

  // New display names (resolved client-side via resolveGroups after selection)
  const newLicenseGroupName = newTitleGroupNames[selectedTitleMapping?.licenseGroup] || selectedTitleMapping?.licenseGroup || "None";
  const newM365Names = [...new Set([
    ...(selectedTitleMapping?.microsoft365Groups || []).map((id) => newTitleGroupNames[id] || id),
    ...(selectedOfficeMapping?.microsoft365Groups || []).map((id) => newOfficeGroupNames[id] || id),
  ])];
  const newSecGroupNames = [...new Set([
    ...(selectedTitleMapping?.securityGroups || []).map((id) => newTitleGroupNames[id] || id),
    ...(selectedOfficeMapping?.securityGroups || []).map((id) => newOfficeGroupNames[id] || id),
  ])];
  const newDeptDLNames = (selectedTitleMapping?.distributionGroups || []).map((id) => newTitleGroupNames[id] || id);
  const newOfficeDLNames = (selectedOfficeMapping?.distributionGroups || []).map((id) => newOfficeGroupNames[id] || id);
  const newAllDLNames = [...new Set([...newDeptDLNames, ...newOfficeDLNames])];

  function ReviewRow({ label, oldVal, newVal, changed }) {
    const unchanged = changed !== undefined ? !changed : oldVal === newVal;
    return (
      <div className={`review-row${unchanged ? " review-row-unchanged" : ""}`}>
        <span className="review-label">{label}</span>
        <span className="review-old">{oldVal || "—"}</span>
        <span className="review-arrow">→</span>
        <span className={`review-new${unchanged ? " review-unchanged-value" : ""}`}>
          {unchanged ? <span className="no-change-badge">No change</span> : (newVal || "—")}
        </span>
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="page-wrapper">
      <header className="app-header">
        <div className="header-inner">
          <div className="header-brand">
            <span className="header-title">Employee Movement</span>
          </div>
          <div className="header-actions">
            <button className="btn-header-ghost" onClick={onViewHistory}>View History</button>
            <span className="header-username">{user.name || submittedBy}</span>
          </div>
        </div>
      </header>

      <main className="form-container">
        <div className="form-card">

          {/* ── Step indicator ── */}
          <div className="step-indicator">
            {["Employee", "New Values", "Review"].map((label, i) => {
              const stepKeys = ["employee", "new-values", "review"];
              const active = step === stepKeys[i];
              const done = stepKeys.indexOf(step) > i;
              return (
                <div key={label} className={`step-item${active ? " step-active" : done ? " step-done" : ""}`}>
                  <div className="step-circle">{done ? "✓" : i + 1}</div>
                  <span className="step-label">{label}</span>
                </div>
              );
            })}
          </div>

          {/* ══ STEP 1: Employee ══ */}
          {step === "employee" && (
            <>
              <div className="form-card-header">
                <h2>Movement Request</h2>
                <p>Search for the employee whose role, department, or location is changing.</p>
              </div>

              <section className="form-section">
                <h3 className="section-title">Employee</h3>
                <div className="form-field" ref={empWrapperRef}>
                  <label className="field-label">Search by name or email</label>
                  <div className="search-wrapper">
                    <input
                      type="text"
                      className="field-input"
                      placeholder="Type at least 3 characters..."
                      value={empQuery}
                      onChange={(e) => handleEmpQueryChange(e.target.value)}
                      autoComplete="off"
                    />
                    {empLoading && <span className="search-spinner" />}
                  </div>
                  {empOpen && empResults.length > 0 && (
                    <ul className="search-dropdown">
                      {empResults.map((u) => (
                        <li key={u.id} className="search-result" onMouseDown={() => handleEmpSelect(u)}>
                          <span className="result-name">{u.displayName}</span>
                          <span className="result-upn">{u.userPrincipalName}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {empOpen && empResults.length === 0 && !empLoading && (
                    <ul className="search-dropdown">
                      <li className="search-result search-no-results">No users found</li>
                    </ul>
                  )}
                </div>
                {validationErrors.employee && <p className="field-error">{validationErrors.employee}</p>}

                {loadingDetails && <p className="loading-hint">Loading employee details...</p>}

                {empDetails && !loadingDetails && (
                  <div className="auto-populated-fields">
                    <div className="fields-grid">
                      <ReadOnlyField label="Display Name" value={empDetails.displayName} />
                      <ReadOnlyField label="User Principal Name" value={empDetails.userPrincipalName} />
                      <ReadOnlyField label="Department" value={empDetails.department} placeholder="Not set" />
                      <ReadOnlyField label="Job Title" value={empDetails.jobTitle} placeholder="Not set" />
                      <ReadOnlyField label="Manager" value={empDetails.managerDisplayName || empDetails.managerUpn} placeholder="Not set" />
                      <ReadOnlyField label="Location" value={empDetails.location} placeholder="Not set" />
                      <ReadOnlyField label="Office" value={empDetails.officeLocation} placeholder="Not set" />
                    </div>
                  </div>
                )}
              </section>

              <div className="form-actions">
                <button
                  className="btn-primary btn-submit"
                  onClick={handleProceedToNewValues}
                  disabled={!empDetails || loadingDetails}
                >
                  Continue
                </button>
              </div>
            </>
          )}

          {/* ══ STEP 2: New Values ══ */}
          {step === "new-values" && (
            <>
              <div className="form-card-header">
                <h2>New Values</h2>
                <p>Select the new department, job title, manager, location, and office for <strong>{empDetails?.displayName}</strong>.</p>
              </div>

              {/* Department */}
              <section className="form-section">
                <h3 className="section-title">New Department <span className="required-star">*</span></h3>
                <SearchableListDropdown
                  label="Search departments"
                  value={deptSearch}
                  onChange={setDeptSearch}
                  onSelect={handleDeptSelect}
                  items={departments}
                  loading={loadingDepts}
                  open={deptOpen}
                  setOpen={setDeptOpen}
                  placeholder="Type to search..."
                  disabled={false}
                  wrapperRef={deptRef}
                />
                {validationErrors.department && <p className="field-error">{validationErrors.department}</p>}
              </section>

              {/* Job Title */}
              <section className="form-section">
                <h3 className="section-title">New Job Title <span className="required-star">*</span></h3>
                <SearchableListDropdown
                  label="Search job titles"
                  value={titleSearch}
                  onChange={setTitleSearch}
                  onSelect={(title) => {
                    const mapping = allTitleMappings.find((t) => t.title === title);
                    if (mapping) handleTitleSelect(mapping);
                  }}
                  items={allTitleMappings.map((t) => t.title)}
                  loading={loadingTitles}
                  open={titleOpen}
                  setOpen={setTitleOpen}
                  placeholder="Type to search..."
                  disabled={!selectedDept}
                  wrapperRef={titleRef}
                />
                {validationErrors.jobTitle && <p className="field-error">{validationErrors.jobTitle}</p>}
              </section>

              {/* Manager */}
              <section className="form-section">
                <h3 className="section-title">New Manager <span className="required-star">*</span></h3>
                <div className="form-field" ref={managerRef}>
                  <label className="field-label">Search by name or email</label>
                  <div className="search-wrapper">
                    <input
                      type="text"
                      className="field-input"
                      placeholder="Type at least 3 characters..."
                      value={managerQuery}
                      onChange={(e) => {
                        setManagerQuery(e.target.value);
                        if (selectedManager) setSelectedManager(null);
                      }}
                      autoComplete="off"
                    />
                    {managerLoading && <span className="search-spinner" />}
                  </div>
                  {managerOpen && managerResults.length > 0 && (
                    <ul className="search-dropdown">
                      {managerResults.map((m) => (
                        <li key={m.id} className="search-result" onMouseDown={() => handleManagerSelect(m)}>
                          <span className="result-name">{m.displayName}</span>
                          <span className="result-upn">{m.userPrincipalName}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {managerOpen && managerResults.length === 0 && !managerLoading && (
                    <ul className="search-dropdown">
                      <li className="search-result search-no-results">No users found</li>
                    </ul>
                  )}
                </div>
                {validationErrors.manager && <p className="field-error">{validationErrors.manager}</p>}
              </section>

              {/* Location */}
              <section className="form-section">
                <h3 className="section-title">New Location <span className="required-star">*</span></h3>
                <div className="form-field">
                  <label className="field-label">Select location</label>
                  <select
                    className="field-input field-select"
                    value={newLocation}
                    onChange={(e) => handleLocationChange(e.target.value)}
                  >
                    <option value="">— Select location —</option>
                    {LOCATIONS.map((l) => (
                      <option key={l} value={l}>{l}</option>
                    ))}
                  </select>
                </div>
                {validationErrors.location && <p className="field-error">{validationErrors.location}</p>}
              </section>

              {/* Office */}
              <section className="form-section">
                <h3 className="section-title">New Office <span className="required-star">*</span></h3>
                {(!newLocation || newLocation === "Remote" || newLocation === "Philadelphia" || newLocation === "New York City") ? (
                  <ReadOnlyField
                    label="Office"
                    value={selectedOfficeMapping?.OfficeName}
                    placeholder={newLocation ? "Auto-populated" : "Select a location first"}
                  />
                ) : (
                  <div className="form-field" ref={officeRef}>
                    <label className="field-label">Search office</label>
                    <div className="search-wrapper">
                      <input
                        type="text"
                        className="field-input"
                        placeholder="Type to search offices..."
                        value={officeSearch}
                        onChange={(e) => { setOfficeSearch(e.target.value); setOfficeOpen(true); }}
                        onFocus={() => setOfficeOpen(true)}
                        autoComplete="off"
                      />
                      {loadingOffices && <span className="search-spinner" />}
                    </div>
                    {officeOpen && allOfficeMappings.length > 0 && (
                      <ul className="search-dropdown">
                        {allOfficeMappings
                          .filter((o) => o.OfficeName.toLowerCase().includes(officeSearch.toLowerCase()))
                          .map((o) => (
                            <li key={o.OfficeName} className="search-result" onMouseDown={async () => {
                              setSelectedOfficeMapping(o);
                              setOfficeSearch(o.OfficeName);
                              setOfficeOpen(false);
                              setValidationErrors((prev) => ({ ...prev, office: undefined }));
                              const names = await resolveGroupNames([
                                ...(o.microsoft365Groups || []),
                                ...(o.securityGroups || []),
                                ...(o.distributionGroups || []),
                              ]);
                              setNewOfficeGroupNames(names);
                            }}>
                              <span className="result-name">{o.OfficeName}</span>
                              {o.Address && <span className="result-upn">{o.Address}</span>}
                            </li>
                          ))}
                      </ul>
                    )}
                  </div>
                )}
                {validationErrors.office && <p className="field-error">{validationErrors.office}</p>}
              </section>

              <div className="form-actions" style={{ justifyContent: "space-between" }}>
                <button className="btn-secondary" onClick={() => setStep("employee")}>Back</button>
                <button className="btn-primary btn-submit" onClick={handleProceedToReview}>
                  Review Changes
                </button>
              </div>
            </>
          )}

          {/* ══ STEP 3: Review ══ */}
          {step === "review" && (
            <>
              <div className="form-card-header">
                <h2>Review Changes</h2>
                <p>Confirm the changes below for <strong>{empDetails?.displayName}</strong> before submitting.</p>
              </div>

              <section className="form-section">
                <div className="review-table">
                  <div className="review-header">
                    <span className="review-label">Field</span>
                    <span className="review-old">Current</span>
                    <span className="review-arrow" />
                    <span className="review-new">New</span>
                  </div>
                  <ReviewRow label="Department" oldVal={empDetails?.department}    newVal={selectedDept} />
                  <ReviewRow label="Job Title"  oldVal={empDetails?.jobTitle}      newVal={selectedTitleMapping?.title} />
                  <ReviewRow label="Manager"    oldVal={empDetails?.managerUpn}    newVal={selectedManager?.userPrincipalName} />
                  <ReviewRow label="Office"     oldVal={empDetails?.officeLocation} newVal={selectedOfficeMapping?.OfficeName} />
                  <ReviewRow
                    label="License Group"
                    oldVal={oldLicenseGroupName}
                    newVal={newLicenseGroupName}
                  />
                  <ReviewRow
                    label="Microsoft 365 Groups"
                    oldVal={(oldTitleMapping || oldOfficeMapping) ? (oldM365Names.length > 0 ? oldM365Names.join(", ") : "No Groups") : "Not found in SP"}
                    newVal={newM365Names.length > 0 ? newM365Names.join(", ") : "No Groups"}
                    changed={[...oldM365Names].sort().join() !== [...newM365Names].sort().join()}
                  />
                  <ReviewRow
                    label="Security Groups"
                    oldVal={(oldTitleMapping || oldOfficeMapping) ? (oldSecGroupNames.length > 0 ? oldSecGroupNames.join(", ") : "No Groups") : "Not found in SP"}
                    newVal={newSecGroupNames.length > 0 ? newSecGroupNames.join(", ") : "No Groups"}
                    changed={[...oldSecGroupNames].sort().join() !== [...newSecGroupNames].sort().join()}
                  />
                  <ReviewRow
                    label="Distribution Groups"
                    oldVal={(oldTitleMapping || oldOfficeMapping) ? (oldAllDLNames.length > 0 ? oldAllDLNames.join(", ") : "No Groups") : "Not found in SP"}
                    newVal={newAllDLNames.length > 0 ? newAllDLNames.join(", ") : "No Groups"}
                    changed={[...oldAllDLs].sort().join() !== [...newAllDLs].sort().join()}
                  />
                </div>
              </section>

              {submitError && (
                <div className="submit-error">
                  <strong>Error:</strong> {submitError}
                </div>
              )}

              <div className="form-actions" style={{ justifyContent: "space-between" }}>
                <button className="btn-secondary" onClick={() => { setStep("new-values"); setSubmitError(""); }} disabled={submitting}>
                  Back
                </button>
                <button className="btn-primary btn-submit" onClick={handleSubmit} disabled={submitting}>
                  {submitting ? (
                    <><span className="btn-spinner" /> Processing...</>
                  ) : (
                    "Confirm & Submit"
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
